import { computed, MaybeRef, unref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { CustomFieldTypeValueInput, RateType } from '@/gql/graphql'
import { JobRateCardBandQuery } from '@/components/Job/graphql/queries'

// Shared by useRateCardBand, useSupplierRateCardBand and useHireRateCardBand — the query,
// debounce and enabled/caching behaviour are identical; only which optional arguments a
// surface sends differ.
export function useJobRateCardBandQuery(options: {
  jobId: MaybeRef<string | null | undefined>
  customFieldValues?: MaybeRef<CustomFieldTypeValueInput[] | undefined>
  rate?: MaybeRef<string | number>
  rateType?: MaybeRef<RateType>
  cached?: boolean
  bandRateType?: MaybeRef<RateType | undefined>
  enabled: MaybeRef<boolean>
}) {
  const { result, loading } = useQuery(
    JobRateCardBandQuery,
    () => ({
      jobId: unref(options.jobId)!,
      customFieldValues: options.customFieldValues ? unref(options.customFieldValues) : undefined,
      rate: options.rate !== undefined ? unref(options.rate) : undefined,
      rateType: options.rateType !== undefined ? unref(options.rateType) : undefined,
      cached: options.cached,
      bandRateType: options.bandRateType !== undefined ? unref(options.bandRateType) : undefined,
    }),
    () => ({
      clientId: 'publicApi',
      debounce: 400,
      enabled: unref(options.enabled) && !!unref(options.jobId),
    }),
  )

  // Disabling only pauses future fetches — Apollo keeps the last resolved result, so without
  // this guard a surface that just turned itself off would keep showing a stale band.
  const band = computed(() =>
    unref(options.enabled) ? (result.value?.job?.rateCardBand ?? null) : null,
  )

  return { band, loading }
}
